import { ModelContract, ModuleDescription } from "@features/shared/module-description";
import { combine, createEffect, createEvent, createStore, sample } from "effector";
import { reset } from "patronum";
import { IStreakContract } from "../model";
import { IEnrollmentsContract } from "@features/enrollments/model";
import { IMetadataContract } from "@features/student/metadata/model";
import { ILessonsContract } from "@features/lessons/model";
import { IUserAuthContract } from "@features/student/auth/model";
import { UserEnrollmentStatus } from "@features/shared/entities/status";
import { dailyGoalChanged as dailyGoalChangedEvent, dailyGoalCompleted as dailyGoalCompletedEvent } from "@features/shared/events";
import { $streakFlowFlag } from "@features/amplitude-flags/model";
import { ICoursesContract } from "@features/courses/model";
import { IRoadmapsContract } from "@features/roadmaps/model";
import { ICourse } from "@features/shared/entities/courses";
import { IRoadmap } from "@features/shared/entities/roadmaps";
import { IStudentLesson, IStudentTopic } from "@features/shared/entities/enrollments";
import { ILesson } from "@features/shared/entities/lessons";
import { CourseDataApi } from "@features/shared/api/course-data.api";
import { routePathNameChanged } from "@features/app/model";

const DEFAULT_DAILY_GOAL = 4;

export type NextLessonInfo = {
    title: string;
    courseIcon: string;
    courseId: number;
    topicId: number;
    lessonId: number;
};

type LessonFlat = ILesson & {
    topicId: number;
    enrollmentStatus: UserEnrollmentStatus | null;
    completedAt: string | null;
};

async function findNextLessonInCourse(
    courseId: number,
    justCompletedLessonId: number | null,
    enrollmentsLessons: IStudentLesson[],
    enrollmentsTopics: IStudentTopic[]
): Promise<LessonFlat | null> {
    const topics = await CourseDataApi.getTopicsByCourseId(courseId);
    const sortedTopics = topics.sort((a, b) => a.number - b.number);
    const allLessons = await Promise.all(
        sortedTopics.map((t) =>
            CourseDataApi.getLessonsByTopicId(t.id).then((ls) => ({
                topicId: t.id,
                lessons: ls.sort((a, b) => a.number - b.number)
            }))
        )
    );

    const enrollmentMap = new Map(enrollmentsLessons.map((e) => [e.lesson_id, e]));
    const flat: LessonFlat[] = allLessons.flatMap(({ topicId, lessons }) =>
        lessons.map((l) => {
            const e = enrollmentMap.get(l.id);
            const status = l.id === justCompletedLessonId ? UserEnrollmentStatus.Completed : (e?.status ?? null);
            return {
                ...l,
                topicId,
                enrollmentStatus: status,
                completedAt: l.id === justCompletedLessonId ? new Date().toISOString() : (e?.completed_at ?? null)
            };
        })
    );

    if (flat.length === 0) return null;

    const isCompleted = (l: LessonFlat) => l.enrollmentStatus === UserEnrollmentStatus.Completed;
    const isCandidate = (l: LessonFlat) =>
        l.enrollmentStatus === null || l.enrollmentStatus === UserEnrollmentStatus.Progress;

    const completedSorted = flat
        .map((l, i) => ({ l, i }))
        .filter(({ l }) => isCompleted(l) && !!l.completedAt)
        .sort((a, b) => new Date(a.l.completedAt!).getTime() - new Date(b.l.completedAt!).getTime());
    const lastIdx = completedSorted.length > 0 ? completedSorted[completedSorted.length - 1].i : -1;

    if (lastIdx !== -1) {
        for (let i = lastIdx + 1; i < flat.length; i++) {
            if (isCandidate(flat[i])) return flat[i];
        }
        for (let i = 0; i < lastIdx; i++) {
            if (isCandidate(flat[i])) return flat[i];
        }
        return null;
    }

    return flat.find((l) => isCandidate(l)) ?? null;
}

const resolveNextLessonInfoFx = createEffect(
    async ({
        completedCourseId,
        completedLessonId,
        enrollmentsLessons,
        enrollmentsTopics,
        allCourses,
        roadmaps
    }: {
        completedCourseId: number;
        completedLessonId: number;
        enrollmentsLessons: IStudentLesson[];
        enrollmentsTopics: IStudentTopic[];
        allCourses: ICourse[];
        roadmaps: IRoadmap[];
    }): Promise<NextLessonInfo | null> => {
        const course = allCourses.find((c) => c.id === completedCourseId);
        if (!course) return null;

        const getCourseIcon = (c: ICourse) => c.small_image ?? c.image ?? "";

        const toInfo = (lesson: LessonFlat, cId: number, c: ICourse): NextLessonInfo => ({
            title: lesson.title,
            courseIcon: getCourseIcon(c),
            courseId: cId,
            topicId: lesson.topicId,
            lessonId: lesson.id
        });

        const nextInCourse = await findNextLessonInCourse(
            completedCourseId,
            completedLessonId,
            enrollmentsLessons,
            enrollmentsTopics
        );
        if (nextInCourse) {
            return toInfo(nextInCourse, completedCourseId, course);
        }

        // Check if the completed course belongs to any roadmap
        const isInRoadmap = roadmaps.some((r) =>
            r.steps.some((s) => s.courses.some((c) => c.id === completedCourseId))
        );
        if (!isInRoadmap) return null;

        const orderedCourses = roadmaps.flatMap((r) =>
            r.steps
                .slice()
                .sort((a, b) => a.number - b.number)
                .flatMap((s) => s.courses.slice().sort((a, b) => a.orderNumber - b.orderNumber))
        );
        const currentIdx = orderedCourses.findIndex((c) => c.id === completedCourseId);
        if (currentIdx === -1) return null;

        for (let i = currentIdx + 1; i < orderedCourses.length; i++) {
            const rc = orderedCourses[i];
            const nextLesson = await findNextLessonInCourse(rc.id, null, enrollmentsLessons, enrollmentsTopics);
            if (nextLesson) {
                const baseCourse = allCourses.find((c) => c.id === rc.id);
                return toInfo(nextLesson, rc.id, baseCourse ?? course);
            }
        }

        for (let i = 0; i < currentIdx; i++) {
            const rc = orderedCourses[i];
            const nextLesson = await findNextLessonInCourse(rc.id, null, enrollmentsLessons, enrollmentsTopics);
            if (nextLesson) {
                const baseCourse = allCourses.find((c) => c.id === rc.id);
                return toInfo(nextLesson, rc.id, baseCourse ?? course);
            }
        }

        return null;
    }
);

const nextLessonInfoRequested = createEvent<{ courseId: number; lessonId: number }>();
const dailyGoalChanged = createEvent<number>();

const trackDailyGoalChangedFx = createEffect(({ before, after }: { before: number; after: number }) => {
    dailyGoalChangedEvent(before, after);
});
const trackDailyGoalCompletedFx = createEffect(() => {
    dailyGoalCompletedEvent();
});

const $dailyGoalLessons = createStore(DEFAULT_DAILY_GOAL);
const $lessonsCompletedToday = createStore(0);
const $dailyGoalReached = createStore(false);
export const $nextLessonInfo = createStore<NextLessonInfo | null>(null)
    .on(resolveNextLessonInfoFx, () => null)
    .reset(routePathNameChanged);

export const $goalComplete = combine(
    $dailyGoalReached,
    $lessonsCompletedToday,
    $dailyGoalLessons,
    (reached, done, goal) => reached || done >= goal
);

sample({
    clock: resolveNextLessonInfoFx.doneData,
    target: $nextLessonInfo
});

const contract = new ModelContract(
    {
        $dailyGoalLessons,
        $lessonsCompletedToday,
        $dailyGoalReached,
        $goalComplete,
        $nextLessonInfo
    },
    {
        nextLessonInfoRequested,
        dailyGoalChanged
    },
    {},
    {}
);

export type IDailyGoalStreakContract = typeof contract;

export class DailyGoalStreakModule extends ModuleDescription<IDailyGoalStreakContract> {
    constructor(
        public streakModule: IStreakContract,
        public enrollmentsModule: IEnrollmentsContract,
        public metadataModule: IMetadataContract,
        public lessonsModule: ILessonsContract,
        public userAuthModule: IUserAuthContract,
        public coursesModule: ICoursesContract,
        public roadmapsModule: IRoadmapsContract
    ) {
        super();
    }

    getDefaultContract() {
        return contract;
    }

    run(): void {
        sample({
            clock: this.streakModule.stores.$streakStats,
            fn: (stats) => {
                const g = stats?.dailyGoal ?? 0;
                return g > 0 ? g : DEFAULT_DAILY_GOAL;
            },
            target: $dailyGoalLessons
        });

        sample({
            clock: dailyGoalChanged,
            target: $dailyGoalLessons
        });

        sample({
            clock: this.streakModule.stores.$streakStats,
            source: $lessonsCompletedToday,
            fn: (current, stats) => Math.max(current, stats?.lessonsCompletedToday ?? 0),
            target: $lessonsCompletedToday
        });

        sample({
            clock: this.streakModule.stores.$streakStats,
            fn: (stats) => stats?.dailyGoalReached ?? false,
            target: $dailyGoalReached
        });

        sample({
            clock: this.lessonsModule.events.lessonCompletionTracked,
            source: $lessonsCompletedToday,
            fn: (count) => count + 1,
            target: $lessonsCompletedToday
        });

        sample({
            clock: dailyGoalChanged,
            source: $dailyGoalLessons,
            fn: (before, after) => ({ before, after }),
            target: trackDailyGoalChangedFx
        });

        sample({
            clock: dailyGoalChanged,
            fn: (n) => ({ key: "daily_goal_lessons", value: String(n) }),
            target: this.metadataModule.events.setMetadataRequested
        });

        sample({
            clock: $goalComplete,
            source: $streakFlowFlag,
            filter: (streakFlow, isComplete) => streakFlow === "daily_goal" && isComplete,
            target: trackDailyGoalCompletedFx
        });

        sample({
            clock: this.lessonsModule.events.lessonCompletionTracked,
            source: {
                streakFlow: $streakFlowFlag,
                lastCompleted: this.lessonsModule.stores.$lastCompletedLessonInfo,
                enrollmentsLessons: this.enrollmentsModule.stores.$enrollmentsLessons,
                enrollmentsTopics: this.enrollmentsModule.stores.$enrollmentsTopics,
                allCourses: this.coursesModule.stores.$allCourses,
                roadmaps: this.roadmapsModule.stores.$roadmaps
            },
            filter: ({ streakFlow, lastCompleted }) =>
                streakFlow === "daily_goal" && lastCompleted !== null,
            fn: (s) => ({
                completedCourseId: s.lastCompleted!.courseId,
                completedLessonId: s.lastCompleted!.lesson.id,
                enrollmentsLessons: s.enrollmentsLessons,
                enrollmentsTopics: s.enrollmentsTopics,
                allCourses: s.allCourses,
                roadmaps: s.roadmaps
            }),
            target: resolveNextLessonInfoFx
        });

        sample({
            clock: nextLessonInfoRequested,
            source: {
                enrollmentsLessons: this.enrollmentsModule.stores.$enrollmentsLessons,
                enrollmentsTopics: this.enrollmentsModule.stores.$enrollmentsTopics,
                allCourses: this.coursesModule.stores.$allCourses,
                roadmaps: this.roadmapsModule.stores.$roadmaps
            },
            fn: (s, { courseId, lessonId }) => ({
                completedCourseId: courseId,
                completedLessonId: lessonId,
                enrollmentsLessons: s.enrollmentsLessons,
                enrollmentsTopics: s.enrollmentsTopics,
                allCourses: s.allCourses,
                roadmaps: s.roadmaps
            }),
            target: resolveNextLessonInfoFx
        });

        reset({
            clock: this.userAuthModule.events.logoutStudent,
            target: [$lessonsCompletedToday, $dailyGoalReached, $nextLessonInfo]
        });
    }
}
